/**
 * STANDARD CARD - Design System 2.0 Component
 *
 * Consistent, beautiful cards with proper variants.
 * Replaces all custom Card implementations.
 */

import React from 'react';
import { Card, CardProps, CardContent, CardHeader, Divider, Box, SxProps, Theme } from '@mui/material';
import { cardVariants } from '../../theme';

export type StandardCardVariant =
  | 'elevated'    // Default elevated card
  | 'glass'       // Glass morphism
  | 'gradient'    // Gradient with mesh
  | 'outlined'    // Minimal outlined
  | 'compact'     // For list items
  | 'interactive'; // Clickable

export type StandardCardSpacing = 'compact' | 'normal' | 'relaxed' | 'generous';

interface StandardCardProps extends Omit<CardProps, 'variant'> {
  variant?: StandardCardVariant;
  spacing?: StandardCardSpacing;
  header?: React.ReactNode;
  showDivider?: boolean;
}

const spacingStyles = {
  compact: { p: 1.5 },
  normal: { p: 2 },
  relaxed: { p: 2.5 },
  generous: { p: 3 },
};

export const StandardCard: React.FC<StandardCardProps> = ({
  variant = 'elevated',
  spacing = 'normal',
  header,
  showDivider = false,
  children,
  sx,
  ...props
}) => {
  const variantSx = cardVariants[variant] || cardVariants.elevated;
  const spacingSx = spacingStyles[spacing];

  return (
    <Card
      sx={[variantSx, sx] as SxProps<Theme>}
      {...props}
    >
      {header && (
        <>
          <CardHeader
            sx={{
              pb: showDivider ? 2 : 0,
            }}
          >
            {header}
          </CardHeader>
          {showDivider && <Divider />}
        </>
      )}
      <CardContent sx={spacingSx}>
        {children}
      </CardContent>
    </Card>
  );
};

// Specialized card variants for common patterns

export const ListCard: React.FC<StandardCardProps> = (props) => (
  <StandardCard variant="elevated" spacing="normal" showDivider {...props} />
);

export const FeatureCard: React.FC<StandardCardProps> = (props) => (
  <StandardCard variant="gradient" spacing="relaxed" {...props} />
);

export const CompactListItem: React.FC<StandardCardProps & { onClick?: () => void }> = ({ onClick, ...props }) => (
  <StandardCard
    variant={onClick ? 'interactive' : 'compact'}
    spacing="compact"
    onClick={onClick}
    sx={{ cursor: onClick ? 'pointer' : 'default', ...props.sx }}
    {...props}
  />
);

export default StandardCard;
